import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getCurrentPackageUser } from "../../../services/api";
import { postNewYearWish } from "../../../services/newYearWishService";
import Notiflix from "notiflix";
import ReCAPTCHA from "react-google-recaptcha";

export default function NewYearWishForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [senderName, setSenderName] = useState("");
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [userName, setUserName] = useState("");
  const [loadingUser, setLoadingUser] = useState(true);
  const [userData, setUserData] = useState(null);
  const [captchaValue, setCaptchaValue] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [jodiacEmail, setJodiacEmail] = useState("");
  const [jodiacPin, setJodiacPin] = useState("");
  const [isJodiacUser, setIsJodiacUser] = useState(null); // null: seçilmedi, true: evet, false: hayır
  const [addInstagram, setAddInstagram] = useState(false);
  const [instagramHandle, setInstagramHandle] = useState("");

  useEffect(() => {
    Notiflix.Notify.init({
      position: "top-center",
      timeout: 3000,
      clickToClose: true,
      pauseOnHover: true,
    });

    // Kullanıcı adını API'den al
    const fetchUserName = async () => {
      try {
        setLoadingUser(true);
        const response = await getCurrentPackageUser(id);
        setUserData(response);
        if (response) {
          const fullName = `${response.first_name || ""} ${
            response.last_name || ""
          }`.trim();
          setUserName(fullName || "Kullanıcı");
        } else {
          setUserName("Kullanıcı");
        }
      } catch (error) {
        console.error("Kullanıcı bilgisi alınırken hata:", error);
        setUserName("Kullanıcı");
      } finally {
        setLoadingUser(false);
      }
    };

    if (id) {
      fetchUserName();
    }
  }, [id]);

  const handleNextStep = () => {
    if (currentStep === 1) {
      if (!senderName.trim()) {
        Notiflix.Notify.warning("Lütfen adınızı ve soyadınızı girin");
        return;
      }
      if (senderName.trim().length < 3) {
        Notiflix.Notify.warning("Ad soyad en az 3 karakter olmalıdır");
        return;
      }
    }
    
    if (currentStep === 2) {
      // Jodiac email ve pin zorunlu değil, boş bırakılabilir
      // Email format kontrolü yapılabilir ama zorunlu değil
      if (jodiacEmail.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(jodiacEmail.trim())) {
        Notiflix.Notify.warning("Lütfen geçerli bir email adresi girin");
        return;
      }
      // PIN sadece rakam olmalı (eğer girildiyse)
      if (jodiacPin.trim() && !/^\d+$/.test(jodiacPin.trim())) {
        Notiflix.Notify.warning("PIN sadece rakamlardan oluşmalıdır");
        return;
      }
    }
    
    if (currentStep === 3) {
      if (!title.trim()) {
        Notiflix.Notify.warning("Lütfen bir başlık girin");
        return;
      }
      if (title.trim().length < 3) {
        Notiflix.Notify.warning("Başlık en az 3 karakter olmalıdır");
        return;
      }
    }
    
    setCurrentStep(currentStep + 1);
  };

  const handlePrevStep = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleCaptchaChange = (value) => {
    setCaptchaValue(value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!message.trim()) {
      Notiflix.Notify.warning("Lütfen dilek mesajınızı yazın");
      return;
    }

    if (message.trim().length < 10) {
      Notiflix.Notify.warning("Dilek mesajınız en az 10 karakter olmalıdır");
      return;
    }

    if (!captchaValue) {
      Notiflix.Notify.warning("Lütfen robot olmadığınızı doğrulayın");
      return;
    }

    try {
      setLoading(true);

      // Yeni yıl tarihini belirle (örnek: 2025-01-01 00:00:00)
      const wishDate = "2025-01-01 00:00:00";

      // API'ye göndermek için veriyi hazırla
      const wishData = {
        title: title.trim(),
        content: message.trim(),
        wish_date: wishDate,
        timezone: "Europe/Istanbul",
        receiver_id: id,
        receiver_mail: userData?.email || "",
        fullname: senderName.trim(), // Harici kullanıcı için
        jodiac_email: jodiacEmail.trim() || null, // Jodiac email (opsiyonel)
        jodiac_pin: jodiacPin.trim() || null, // Jodiac pin (opsiyonel)
        social_media: instagramHandle.trim() || null, // Instagram handle (opsiyonel)
      };

      // API çağrısı
      const response = await postNewYearWish(wishData);

      if (response.success) {
        setShowSuccess(true);
      } else {
        throw new Error(response.message || "Dilek gönderilemedi");
      }
    } catch (error) {
      console.error("Dilek gönderilirken hata:", error);
      const errorMessage = error.response?.data?.message || error.message || "Dilek gönderilirken bir hata oluştu!";
      Notiflix.Notify.failure(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden bg-gradient-to-br from-red-700 via-red-600 to-red-800">
      {/* Animated Background Patterns */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        {/* Snow/Light Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-white/5 via-transparent to-black/20"></div>
        
        {/* Christmas Trees - Left Bottom (Mobile Optimized) */}
        <div className="absolute bottom-0 -left-8 opacity-25" style={{ transform: 'scale(0.6)' }}>
          <svg width="200" height="250" viewBox="0 0 200 250" fill="none">
            <path d="M100 20 L140 80 L130 80 L160 130 L145 130 L175 190 L25 190 L55 130 L40 130 L70 80 L60 80 Z" fill="#0f5132" />
            <rect x="90" y="190" width="20" height="40" fill="#4a3020" />
          </svg>
        </div>
        
        {/* Christmas Trees - Right Bottom (Mobile Optimized) */}
        <div className="absolute bottom-0 -right-8 opacity-25" style={{ transform: 'scale(0.6)' }}>
          <svg width="200" height="250" viewBox="0 0 200 250" fill="none">
            <path d="M100 20 L140 80 L130 80 L160 130 L145 130 L175 190 L25 190 L55 130 L40 130 L70 80 L60 80 Z" fill="#0f5132" />
            <rect x="90" y="190" width="20" height="40" fill="#4a3020" />
          </svg>
        </div>
        
        {/* Christmas Trees - Top Corners (Very Small for Mobile) */}
        <div className="absolute top-4 left-2 opacity-15" style={{ transform: 'scale(0.3) rotate(-15deg)' }}>
          <svg width="200" height="250" viewBox="0 0 200 250" fill="none">
            <path d="M100 20 L140 80 L130 80 L160 130 L145 130 L175 190 L25 190 L55 130 L40 130 L70 80 L60 80 Z" fill="#0f5132" />
          </svg>
        </div>
        <div className="absolute top-8 right-2 opacity-15" style={{ transform: 'scale(0.25) rotate(12deg)' }}>
          <svg width="200" height="250" viewBox="0 0 200 250" fill="none">
            <path d="M100 20 L140 80 L130 80 L160 130 L145 130 L175 190 L25 190 L55 130 L40 130 L70 80 L60 80 Z" fill="#0f5132" />
          </svg>
        </div>
        
        {/* Ornaments / Lights - Mobile Positioned */}
        <div className="absolute top-20 left-6 w-3 h-3 bg-yellow-300 rounded-full animate-pulse shadow-lg shadow-yellow-300/50"></div>
        <div className="absolute top-32 right-8 w-2.5 h-2.5 bg-green-300 rounded-full animate-pulse shadow-lg shadow-green-300/50" style={{ animationDelay: '0.5s' }}></div>
        <div className="absolute top-44 left-12 w-3 h-3 bg-blue-300 rounded-full animate-pulse shadow-lg shadow-blue-300/50" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-56 right-6 w-3 h-3 bg-yellow-300 rounded-full animate-pulse shadow-lg shadow-yellow-300/50" style={{ animationDelay: '1.5s' }}></div>
        <div className="absolute bottom-32 left-8 w-2.5 h-2.5 bg-red-300 rounded-full animate-pulse shadow-lg shadow-red-300/50" style={{ animationDelay: '2s' }}></div>
        <div className="absolute bottom-48 right-10 w-3 h-3 bg-orange-300 rounded-full animate-pulse shadow-lg shadow-orange-300/50" style={{ animationDelay: '2.5s' }}></div>
        
        {/* Golden Stars - Mobile Positioned */}
        <div className="absolute top-12 right-12 text-yellow-300 opacity-60 animate-pulse">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
          </svg>
        </div>
        <div className="absolute top-1/3 left-8 text-yellow-300 opacity-60 animate-pulse" style={{ animationDelay: '0.7s' }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
          </svg>
        </div>
        <div className="absolute top-1/2 right-16 text-yellow-300 opacity-60 animate-pulse" style={{ animationDelay: '1.3s' }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
          </svg>
        </div>
        <div className="absolute bottom-1/3 left-1/4 text-yellow-300 opacity-50 animate-pulse" style={{ animationDelay: '1.8s' }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
          </svg>
        </div>
        
        {/* Snowflakes - Mobile Positioned */}
        <div className="absolute top-16 right-1/3 text-white/40 animate-ping">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2L12 22M12 2L7 7M12 2L17 7M12 22L7 17M12 22L17 17M2 12L22 12M2 12L7 7M2 12L7 17M22 12L17 7M22 12L17 17M6 6L18 18M6 18L18 6" stroke="currentColor" strokeWidth="1.5" fill="none" />
          </svg>
        </div>
        <div className="absolute top-2/3 left-8 text-white/30 animate-ping" style={{ animationDelay: '0.8s' }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2L12 22M12 2L7 7M12 2L17 7M12 22L7 17M12 22L17 17M2 12L22 12M2 12L7 7M2 12L7 17M22 12L17 7M22 12L17 17M6 6L18 18M6 18L18 6" stroke="currentColor" strokeWidth="1.5" fill="none" />
          </svg>
        </div>
        <div className="absolute bottom-1/4 right-12 text-white/35 animate-ping" style={{ animationDelay: '1.5s' }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2L12 22M12 2L7 7M12 2L17 7M12 22L7 17M12 22L17 17M2 12L22 12M2 12L7 7M2 12L7 17M22 12L17 7M22 12L17 17M6 6L18 18M6 18L18 6" stroke="currentColor" strokeWidth="1.5" fill="none" />
          </svg>
        </div>
      </div>

      <div className="relative z-10 min-h-screen flex items-center justify-center py-8 px-4">
        {/* Ana Sayfa Butonu - Sol Üst */}
        <button
          onClick={() => navigate("/login")}
          className="absolute top-4 left-4 z-20 p-2.5 bg-white/90 hover:bg-white rounded-full shadow-lg transition-all duration-300 hover:scale-110"
          aria-label="Ana Sayfa"
        >
          <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
          </svg>
        </button>

        <div className="w-full max-w-md">
          {!showSuccess ? (
            <>
              {/* Header - Kırmızı Arkaplanda */}
              <div className="text-center space-y-3 mb-6">
                <h1 className="text-white text-3xl sm:text-4xl font-bold leading-tight drop-shadow-lg">
                  Mutlu Yıllar! 
                </h1>
                
                {!loadingUser && userName && (
                  <p className="text-white/95 text-sm font-medium leading-relaxed px-2 drop-shadow-md">
                    <span className="font-bold text-yellow-300">{userName}</span> sizden yeni yıl dileği bekliyor! Hemen onu mutlu edecek güzel bir dilek mesajı yazın. ✨
                  </p>
                )}
                {loadingUser && (
                  <div className="flex items-center justify-center gap-2">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    <p className="text-white/90 text-sm">Yükleniyor...</p>
                  </div>
                )}
              </div>

              {/* Progress Indicator */}
              <div className="flex items-center justify-center mb-6 gap-2">
                {[1, 2, 3, 4].map((step) => (
                  <div key={step} className="flex items-center">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm transition-all duration-300 ${
                        currentStep === step
                          ? "bg-white text-red-600 scale-110"
                          : currentStep > step
                          ? "bg-green-500 text-white"
                          : "bg-white/30 text-white"
                      }`}
                    >
                      {currentStep > step ? "✓" : step}
                    </div>
                    {step < 4 && (
                      <div
                        className={`w-8 h-1 mx-1 rounded transition-all duration-300 ${
                          currentStep > step ? "bg-green-500" : "bg-white/30"
                        }`}
                      />
                    )}
                  </div>
                ))}
              </div>

              {/* Glassmorphism Card */}
              <div className="backdrop-blur-xl bg-white/95 rounded-3xl shadow-2xl border border-white/70 p-5 sm:p-8 transform transition-all duration-500">
                {/* Step 1: Ad Soyad */}
                {currentStep === 1 && (
                  <div className="space-y-6">
                    <div className="text-center">
                      <h2 className="text-2xl font-bold text-red-700 mb-2">Dilek Sahibi</h2>
                      <p className="text-gray-600 text-sm">Adınızı ve soyadınızı girin</p>
                    </div>
                    <div className="relative">
                      <input
                        type="text"
                        value={senderName}
                        onChange={(e) => setSenderName(e.target.value)}
                        placeholder="Adınız Soyadınız"
                        className="w-full px-4 py-4 text-base border-2 border-gray-200 rounded-2xl focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-100 transition-all duration-300 bg-white backdrop-blur-sm placeholder:text-gray-400 text-gray-900 font-medium shadow-sm"
                        maxLength={50}
                        autoFocus
                      />
                    </div>
                    
                    {/* Instagram Checkbox */}
                    <div className="space-y-3">
                      <label className="flex items-center gap-2 cursor-pointer group">
                        <input
                          type="checkbox"
                          checked={addInstagram}
                          onChange={(e) => {
                            setAddInstagram(e.target.checked);
                            if (!e.target.checked) {
                              setInstagramHandle("");
                            }
                          }}
                          className="w-5 h-5 text-red-600 bg-white border-2 border-gray-300 rounded focus:ring-red-500 focus:ring-2 cursor-pointer transition-all duration-200 checked:bg-red-600 checked:border-red-600 appearance-none relative after:content-['✓'] after:absolute after:inset-0 after:flex after:items-center after:justify-center after:text-white after:text-xs after:font-bold after:opacity-0 checked:after:opacity-100"
                        />
                        <span className="text-gray-700 font-medium group-hover:text-red-600 transition-colors">Instagram eklemek ister misin?</span>
                      </label>
                      
                      {/* Instagram Input - Sadece checkbox işaretliyse göster */}
                      {addInstagram && (
                        <div className="relative">
                          <input
                            type="text"
                            value={instagramHandle}
                            onChange={(e) => {
                              // @ işaretini otomatik ekle veya kaldır
                              let value = e.target.value;
                              if (value && !value.startsWith('@')) {
                                value = '@' + value.replace(/^@+/, '');
                              }
                              setInstagramHandle(value);
                            }}
                            placeholder="@instagram"
                            className="w-full px-4 py-4 text-base border-2 border-gray-200 rounded-2xl focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-100 transition-all duration-300 bg-white backdrop-blur-sm placeholder:text-gray-400 text-gray-900 font-medium shadow-sm"
                            maxLength={30}
                          />
                        </div>
                      )}
                    </div>
                    
                    <button
                      type="button"
                      onClick={handleNextStep}
                      disabled={!senderName.trim()}
                      className={`w-full py-4 rounded-2xl font-bold text-white text-lg transition-all duration-300 transform ${
                        !senderName.trim()
                          ? "bg-gray-300 cursor-not-allowed"
                          : "bg-red-600 hover:bg-red-700 active:scale-[0.97] shadow-lg"
                      }`}
                    >
                      Devam Et →
                    </button>
                  </div>
                )}

                {/* Step 2: Jodiac'lı mısın? */}
                {currentStep === 2 && (
                  <div className="space-y-6">
                    <div className="text-center">
                      <h2 className="text-2xl font-bold text-red-700 mb-2">Jodiac'lı mısın?</h2>
                    </div>
                    <div className="space-y-4">
                      <p className="text-gray-600 text-sm leading-relaxed text-center">
                        Eğer Jodiac sahibiysen listelenirken renklenirsin. Ayrıca sen de arkadaşlarından dilek alabilirsin.
                      </p>
                      
                      {/* Checkbox'lar - Sadece Evet seçildiğinde gizle */}
                      {isJodiacUser !== true && (
                        <div className="flex gap-6 justify-center">
                          <label className="flex items-center gap-2 cursor-pointer group">
                            <input
                              type="checkbox"
                              checked={isJodiacUser === true}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setIsJodiacUser(true);
                                } else {
                                  setIsJodiacUser(null);
                                }
                              }}
                              className="w-5 h-5 text-red-600 bg-white border-2 border-gray-300 rounded focus:ring-red-500 focus:ring-2 cursor-pointer transition-all duration-200 checked:bg-red-600 checked:border-red-600 appearance-none relative after:content-['✓'] after:absolute after:inset-0 after:flex after:items-center after:justify-center after:text-white after:text-xs after:font-bold after:opacity-0 checked:after:opacity-100"
                            />
                            <span className="text-gray-700 font-medium group-hover:text-red-600 transition-colors">Evet</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer group">
                            <input
                              type="checkbox"
                              checked={isJodiacUser === false}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setIsJodiacUser(false);
                                  setJodiacEmail("");
                                  setJodiacPin("");
                                } else {
                                  setIsJodiacUser(null);
                                }
                              }}
                              className="w-5 h-5 text-red-600 bg-white border-2 border-gray-300 rounded focus:ring-red-500 focus:ring-2 cursor-pointer transition-all duration-200 checked:bg-red-600 checked:border-red-600 appearance-none relative after:content-['✓'] after:absolute after:inset-0 after:flex after:items-center after:justify-center after:text-white after:text-xs after:font-bold after:opacity-0 checked:after:opacity-100"
                            />
                            <span className="text-gray-700 font-medium group-hover:text-red-600 transition-colors">Hayır</span>
                          </label>
                        </div>
                      )}

                      {/* Email ve PIN Input - Sadece Evet seçilirse göster */}
                      {isJodiacUser === true && (
                        <div className="space-y-3">
                          <div className="relative">
                            <input
                              type="email"
                              value={jodiacEmail}
                              onChange={(e) => setJodiacEmail(e.target.value)}
                              placeholder="Jodiac Kayıtlı Mail Adresi"
                              className="w-full px-4 py-4 text-base border-2 border-gray-200 rounded-2xl focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-100 transition-all duration-300 bg-white backdrop-blur-sm placeholder:text-gray-400 text-gray-900 font-medium shadow-sm"
                              autoFocus
                            />
                          </div>
                          <div className="relative">
                            <input
                              type="text"
                              value={jodiacPin}
                              onChange={(e) => setJodiacPin(e.target.value)}
                              placeholder="Jodiac PIN"
                              className="w-full px-4 py-4 text-base border-2 border-gray-200 rounded-2xl focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-100 transition-all duration-300 bg-white backdrop-blur-sm placeholder:text-gray-400 text-gray-900 font-medium shadow-sm"
                              maxLength={4}
                            />
                          </div>
                        </div>
                      )}
                      
                      <div className="text-center">
                        <a
                          href="https://www.jodi.ac"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-red-600 hover:text-red-700 text-sm font-semibold underline transition-colors"
                        >
                          Jodiac'a Katıl! Şık Bilekliğin İle Farklısın!
                        </a>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={handlePrevStep}
                        className="flex-1 py-4 rounded-2xl font-bold text-red-600 bg-red-50 hover:bg-red-100 transition-all duration-300"
                      >
                        ← Geri
                      </button>
                      <button
                        type="button"
                        onClick={handleNextStep}
                        className="flex-1 py-4 rounded-2xl font-bold text-white text-lg transition-all duration-300 transform bg-red-600 hover:bg-red-700 active:scale-[0.97] shadow-lg"
                      >
                        Devam Et →
                      </button>
                    </div>
                  </div>
                )}

                {/* Step 3: Dilek Başlığı */}
                {currentStep === 3 && (
                  <div className="space-y-6">
                    <div className="text-center">
                      <h2 className="text-2xl font-bold text-red-700 mb-2">Dilek Başlığı</h2>
                      <p className="text-gray-600 text-sm">Dileğinize bir başlık verin</p>
                    </div>
                    <div className="relative">
                      <input
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder="Örn: Mutlu Yıllar! 🎉"
                        className="w-full px-4 py-4 text-base border-2 border-gray-200 rounded-2xl focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-100 transition-all duration-300 bg-white backdrop-blur-sm placeholder:text-gray-400 text-gray-900 font-medium shadow-sm"
                        maxLength={100}
                        autoFocus
                      />
                    </div>
                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={handlePrevStep}
                        className="flex-1 py-4 rounded-2xl font-bold text-red-600 bg-red-50 hover:bg-red-100 transition-all duration-300"
                      >
                        ← Geri
                      </button>
                      <button
                        type="button"
                        onClick={handleNextStep}
                        disabled={!title.trim()}
                        className={`flex-1 py-4 rounded-2xl font-bold text-white text-lg transition-all duration-300 transform ${
                          !title.trim()
                            ? "bg-gray-300 cursor-not-allowed"
                            : "bg-red-600 hover:bg-red-700 active:scale-[0.97] shadow-lg"
                        }`}
                      >
                        Devam Et →
                      </button>
                    </div>
                  </div>
                )}

                {/* Step 4: Mesaj + reCAPTCHA */}
                {currentStep === 4 && (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="text-center">
                      <h2 className="text-2xl font-bold text-red-700 mb-2">Dilek Mesajı</h2>
                      <p className="text-gray-600 text-sm">Yeni yıl mesajınızı yazın</p>
                    </div>
                    <div className="relative">
                      <textarea
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder="Yeni yıl mesajınızı buraya yazın... 💫"
                        rows={6}
                        className="w-full px-4 py-4 text-base border-2 border-gray-200 rounded-2xl focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-100 transition-all duration-300 resize-none bg-white backdrop-blur-sm text-gray-900 placeholder:text-gray-400 font-medium shadow-sm"
                        maxLength={500}
                        autoFocus
                      />
                      <div className="absolute bottom-3 right-3">
                        <p className="text-xs font-semibold text-gray-400 bg-white/90 backdrop-blur-sm px-2.5 py-1 rounded-full shadow-sm">
                          {message.length} / 500
                        </p>
                      </div>
                    </div>

                    {/* Google reCAPTCHA */}
                    <div className="flex justify-center">
                      <ReCAPTCHA
                        sitekey="6Lfu7xQsAAAAAHiiIDcwV0G-TaupqAukBBm-NTSA"
                        onChange={handleCaptchaChange}
                        theme="light"
                      />
                    </div>

                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={handlePrevStep}
                        disabled={loading}
                        className="flex-1 py-4 rounded-2xl font-bold text-red-600 bg-red-50 hover:bg-red-100 transition-all duration-300 disabled:opacity-50"
                      >
                        ← Geri
                      </button>
                      <button
                        type="submit"
                        disabled={loading || !message.trim() || !captchaValue}
                        className={`flex-1 py-4 rounded-2xl font-bold text-white text-lg transition-all duration-300 transform ${
                          loading || !message.trim() || !captchaValue
                            ? "bg-gray-300 cursor-not-allowed"
                            : "bg-red-600 hover:bg-red-700 active:scale-[0.97] shadow-lg"
                        }`}
                      >
                        {loading ? (
                          <span className="flex items-center justify-center gap-2">
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                            Gönderiliyor...
                          </span>
                        ) : (
                          <span className="flex items-center justify-center gap-2">
                            Gönder
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                            </svg>
                          </span>
                        )}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </>
          ) : (
            /* Success Screen */
            <div className="backdrop-blur-xl bg-white/95 rounded-3xl shadow-2xl border border-white/70 p-8 sm:p-12 transform transition-all duration-500 text-center space-y-6">

              {/* Success Message */}
              <div className="space-y-3">
                <h2 className="text-3xl sm:text-4xl font-bold text-red-700">Tebrikler! 🎉</h2>
                <p className="text-gray-700 text-base sm:text-lg font-medium">
                  Yeni yıl dileğiniz başarıyla <span className="font-bold text-yellow-600">{userName}</span>'e gönderildi!
                </p>
              </div>

              {/* Divider */}
              <div className="border-t-2 border-gray-200 my-6"></div>

              {/* Jodiac Promotion */}
              <div className="space-y-4 bg-gradient-to-br from-red-50 to-orange-50 p-6 rounded-2xl border-2 border-red-200">
                <h3 className="text-xl font-bold text-red-700">Sen de dilek almak ister misin?</h3>
                <p className="text-gray-700 text-sm leading-relaxed">
                  <span className="font-bold text-red-600">Jodiac</span>, şık ve akıllı bilekliğin ile farklısın! ✨
                </p>
                <a
                  href="https://www.jodi.ac"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block w-full py-3 px-6 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg"
                >
                  Hemen Satın Al →
                </a>
              </div>
            </div>
          )}

          {/* Jodiac Branding - Kırmızı Arkaplanda */}
          {!showSuccess && (
            <div className="flex flex-col items-center justify-center mt-6 space-y-2">
              <img 
                src="/assets/pngImages/jodiacLogoML.png" 
                alt="Jodiac Logo" 
                className="h-10 sm:h-12 w-auto opacity-90 hover:opacity-100 transition-opacity"
              />
              <a 
                href="https://www.jodi.ac" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-white text-sm sm:text-base font-semibold hover:text-yellow-300 transition-colors"
              >
                www.jodi.ac
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
